import { type CSSProperties } from "react";

import { type Chart, type ChartEvent } from "chart.js";
import { groupBy, keyBy, orderBy } from "lodash";

import { isAnnotationDataset } from "@features/custom-annotations";
import {
  COMPARE_METRIC_1_BENCHMARK_ID,
  COMPARE_METRIC_1_ID,
  COMPARE_METRIC_2_BENCHMARK_ID,
  COMPARE_METRIC_2_ID,
  MAIN_METRIC_BENCHMARK_ID,
  MAIN_METRIC_ID,
  isBarChartCheck,
} from "@features/sales-v3/utils/chart";
import {
  buildCompareNameAndIconHtml,
  buildLegend,
  buildNameAndIconHtml,
  buildTooltipContainer,
  findCompareQueryMetadata,
  findOrCreateTooltip,
  formatTooltipDate,
  getCurrentMetric,
} from "@features/sales-v3/utils/tooltip";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import { type CurrencyCode } from "@north-beam/nb-common";

import {
  type CustomChartDataset,
  getChartDataManagerOptions,
  isChartAggregateView,
  isChartSplitView,
} from "./chart-data-manager-plugin";

export const X_AXIS_SUMMARY_TOOLTIP_PLUGIN_ID = "xAxisSummaryTooltip";

const getHoveredLabelIndex = (chart: Chart, event: ChartEvent) =>
  (chart.scales.x as any)._labelItems?.findIndex((item: any) => {
    const [x, y] = item.translation;
    const height = 36;
    const width = 52;
    if (event.x && event.y) {
      return (
        event.x >= x - width / 2 &&
        event.x <= x + width / 2 &&
        event.y >= y - height / 2 &&
        event.y <= y + height / 2
      );
    }
    return false;
  });

function findSubsetIndexes<T>(mainList: T[], subset: T[]): number[] {
  const indexMap = new Map<T, number>();
  const indexes: number[] = [];

  // Create a map of mainList elements and their indexes
  for (let i = 0; i < mainList.length; i++) {
    indexMap.set(mainList[i], i);
  }

  // Find the indexes of subset elements using the map
  for (const item of subset) {
    const index = indexMap.get(item);
    if (index !== undefined) {
      indexes.push(index);
    }
  }

  return indexes;
}

export const xAxisSummaryTooltipPlugin = (code: CurrencyCode) => ({
  id: X_AXIS_SUMMARY_TOOLTIP_PLUGIN_ID,
  afterEvent(chart: Chart<any>, { event }: { event: ChartEvent }) {
    const tooltipEl = findOrCreateTooltip("sales-v3-summary-tooltip");

    const metricDatasetIdToBenchmarkId: Record<string, string> = {
      [MAIN_METRIC_ID]: MAIN_METRIC_BENCHMARK_ID,
      [COMPARE_METRIC_1_ID]: COMPARE_METRIC_1_BENCHMARK_ID,
      [COMPARE_METRIC_2_ID]: COMPARE_METRIC_2_BENCHMARK_ID,
    };

    const datasets = chart.data.datasets.filter(
      (dataset) =>
        !(
          isAnnotationDataset(dataset) ||
          dataset.realID === MAIN_METRIC_BENCHMARK_ID ||
          dataset.realID === COMPARE_METRIC_1_BENCHMARK_ID ||
          dataset.realID === COMPARE_METRIC_2_BENCHMARK_ID
        ),
    );
    const benchmarkDatasets: Record<string, any> = chart.data.datasets
      .filter(
        (dataset) =>
          dataset.realID === MAIN_METRIC_BENCHMARK_ID ||
          dataset.realID === COMPARE_METRIC_1_BENCHMARK_ID ||
          dataset.realID === COMPARE_METRIC_2_BENCHMARK_ID,
      )
      .reduce((acc, e) => {
        acc[e.realID] = e;
        return acc;
      }, {});
    const chartDataManagerOptions = getChartDataManagerOptions(chart);
    const {
      chartConfig: { comparisons },
      selectedMetrics,
    } = chartDataManagerOptions;

    const tickItems = (chart.scales.x as any).ticks;

    const completeLabelsList = chart.config.data.labels?.map((i: any) =>
      i.getTime(),
    ) as number[];

    const matchingIndexes = findSubsetIndexes(
      completeLabelsList,
      tickItems.map((i: any) => i.value),
    );

    const chartLabelIndex = getHoveredLabelIndex(chart, event);
    const labelItems = (chart.scales.x as any)._labelItems;
    const chartLabel = labelItems?.[chartLabelIndex];

    if (
      chartLabelIndex === -1 ||
      !chartLabel ||
      selectedMetrics.length === 0 ||
      datasets.length === 0
    ) {
      tooltipEl.style.opacity = "0";
      return;
    }

    const isAggregateView = isChartAggregateView(chart);
    const { timeGranularity, dateRangeInterval } =
      chartDataManagerOptions.pageConfiguration;
    const date = formatTooltipDate(
      tickItems[chartLabelIndex].value as number,
      timeGranularity,
      dateRangeInterval,
    );

    const numOfComparisons = comparisons.length + 1;
    const html = [];
    const isComparingDateRange =
      comparisons.length > 0 && comparisons[0].queryKey === "compareDateRange";

    const formatTooltipDateForTimeComparison = (dataset: any) => {
      const {
        labels,
        compareDateRangeLabels,
        pageConfiguration: { timeGranularity, dateRangeInterval },
      } = chartDataManagerOptions;

      let dates = labels;

      if (dataset.compare?.queryKey && compareDateRangeLabels) {
        dates = compareDateRangeLabels;
      }

      const dataIndex = matchingIndexes[chartLabelIndex];

      return formatTooltipDate(
        dates[dataIndex],
        timeGranularity,
        dateRangeInterval,
      );
    };

    const dataIndex = matchingIndexes[chartLabelIndex];
    if (isAggregateView) {
      const sortedDatasetWithMetric = orderBy(
        datasets.map((dataset, index) => ({
          metric: selectedMetrics[index],
          dataset,
        })),
        ({ dataset }) => dataset.data[dataIndex],
        "desc",
      );
      if (selectedMetrics.length === 1) {
        const [mainMetric] = selectedMetrics;
        const headerHtml = [
          `<div class="font-semibold">${mainMetric.name}</div>`,
          `<div class="text-xs text-gray+2">${date}</div>`,
        ];

        if (isComparingDateRange) {
          headerHtml.pop();
        }

        html.push(...headerHtml);

        if (comparisons.length === 0) {
          // Aggregate view, one metric, no comparisons
          const [{ dataset }] = sortedDatasetWithMetric;
          const value = dataset.data[dataIndex];
          const benchmarkValue =
            benchmarkDatasets[MAIN_METRIC_BENCHMARK_ID]?.data[dataIndex];
          let dataHtml = `
            <div>${buildNameAndIconHtml(chart, dataset)}</div>
            <div class="font-medium font-mono text-xs justify-self-end ml-2">
              ${mainMetric.formatter(value, code, {
                precision: "exact",
              })}
            </div>`;

          if (benchmarkValue) {
            dataHtml += `
              <div class="font-medium font-mono text-xs justify-self-end ml-2">
                Benchmark: ${mainMetric.formatter(benchmarkValue, code, {
                  precision: "exact",
                })}
              </div>`;
          }
          html.push(
            `<div class="items-center mt-1 gap-1 grid grid-cols-${
              benchmarkValue ? "3" : "2"
            }">${dataHtml}</div>`,
          );
        } else {
          // Aggregate view, one metric with comparison
          const dataHtml: string[] = [];
          sortedDatasetWithMetric.forEach(({ dataset }) => {
            const benchmarkDatasetId =
              metricDatasetIdToBenchmarkId[dataset.realID as string];
            const benchmarkValue =
              benchmarkDatasets[benchmarkDatasetId ?? ""]?.data[dataIndex];
            const queryMetadata = findCompareQueryMetadata(
              dataset,
              chartDataManagerOptions,
            );
            if (queryMetadata && isComparingDateRange) {
              queryMetadata.optionLabel =
                formatTooltipDateForTimeComparison(dataset);
            }
            const compareNameAndIconHtml = buildCompareNameAndIconHtml({
              queryMetadata,
              dataset,
              hasComparisons: comparisons.length > 0,
            });

            dataHtml.push(`
              <div class="flex items-center gap-2">
                <div class="w-[40px]">${buildNameAndIconHtml(
                  chart,
                  dataset,
                )}</div>
                ${compareNameAndIconHtml}
                <div class="w-[160px] font-medium font-mono text-xs text-right">
                  ${mainMetric.formatter(dataset.data[dataIndex], code, {
                    precision: "exact",
                  })}
                </div>
            `);

            if (benchmarkValue) {
              dataHtml.push(`
                <div class="font-medium font-mono text-xs text-right ml-2">
                  Benchmark: ${mainMetric.formatter(benchmarkValue, code, {
                    precision: "exact",
                  })}
                </div>`);
            }

            dataHtml.push("</div>");
          });

          html.push(
            `<div class="flex flex-col mt-1 gap-y-1 gap-x-3">
              ${dataHtml.join("")}
            </div>`,
          );
        }
      } else {
        // Aggregate view, 2 or more different metrics
        const headerHtml = `<div class="font-semibold">${date}</div>`;
        html.push(headerHtml);

        const dataHtml: string[] = [];
        sortedDatasetWithMetric.forEach(({ dataset, metric }) => {
          const benchmarkDatasetId =
            metricDatasetIdToBenchmarkId[dataset.realID as string];
          const benchmarkValue =
            benchmarkDatasets[benchmarkDatasetId ?? ""]?.data[dataIndex];

          dataHtml.push(`
            <div class="flex items-center gap-2">
              <div class="w-[40px]">${buildNameAndIconHtml(
                chart,
                dataset,
              )}</div>
              <span class="w-[120px] text-interstellar text-xs pr-3">
                ${metric.name}
              </span>
              <div class="font-medium font-mono text-xs text-right">
                ${metric.formatter(dataset.data[dataIndex], code, {
                  precision: "exact",
                })}
              </div>
          `);

          if (benchmarkValue) {
            dataHtml.push(`
              <div class="font-medium font-mono text-xs text-right ml-2">
                Benchmark: ${metric.formatter(benchmarkValue, code, {
                  precision: "exact",
                })}
              </div>`);
          }

          dataHtml.push("</div>");
        });

        html.push(
          `<div class="flex flex-col mt-1 gap-y-1 gap-x-3">
            ${dataHtml.join("")}
          </div>`,
        );
      }
    } else {
      // Split view, one or more metrics
      const headerHtml = `<div class="text-left font-semibold px-2 pb-1 mb-1 border-b">${date}</div>`;
      if (!isComparingDateRange) {
        html.push(headerHtml);
      }

      const groupedDatasets = orderBy(
        Object.values(groupBy(datasets, "name")),
        (datasets) => datasets[0].data[dataIndex],
        "desc",
      );

      const dataHtml = groupedDatasets.reduce(
        (acc: string, datasets: CustomChartDataset[]) => {
          const row: string[] = [];

          const ordering = [
            MAIN_METRIC_ID,
            COMPARE_METRIC_1_ID,
            COMPARE_METRIC_2_ID,
          ].slice(0, numOfComparisons);
          const datasetsById = keyBy(datasets, "realID");

          ordering.forEach((metricId) => {
            const dataset = datasetsById[metricId];
            if (!dataset) {
              row.push(`
              <td align="right" class="border border-gray-2 px-2 py-1 border-t-0 last:border-r-0">
                <div class="flex items-center"></div>
              </td>
            `);
              return;
            }
            const { formatter } = getCurrentMetric(
              dataset,
              getChartDataManagerOptions(chart),
            );

            const value = dataset.data[dataIndex];
            const formattedValue = formatter(value, code, {
              precision: "exact",
            });

            const legend = `
              <div class="w-6">
                ${buildLegend(
                  {
                    borderColor: dataset.baseBorderColor,
                    backgroundColor: dataset.baseBackgroundColor,
                    borderDash: dataset.borderDash,
                  },
                  isBarChartCheck(chart),
                )}
              </div>`;

            row.push(`
              <td align="right" class="border border-gray-2 px-2 py-1 border-t-0 last:border-r-0">
                <div class="flex items-center">
                  ${legend}
                  <span class="font-medium font-mono text-xs pl-1">
                    ${formattedValue}
                  </span>
                </div>
              </td>
            `);
          });

          const dataset = datasets[0];

          return (
            acc +
            `
            <tr class="odd:bg-cosmic-5">
              <td align="center" class="border border-gray-2 border-t-0 first:border-l-0 px-2">
                ${buildNameAndIconHtml(chart, dataset)}
              </td>
              ${row.join("")}
            </tr>`
          );
        },
        "",
      );

      html.push(`
        <table class="w-full">
          <thead>
            <tr>
              <th></th>
              ${
                selectedMetrics.length === numOfComparisons
                  ? selectedMetrics
                      .map(
                        (metric: SalesPageMetric) =>
                          `<th class="text-right text-xs font-semibold px-2 relative">
                            <span class="absolute left-0 h-3/4 border-l"></span>
                            ${metric.name}
                          </th>`,
                      )
                      .join("")
                  : groupedDatasets[0]
                      .map((dataset) => {
                        const queryMetadata = findCompareQueryMetadata(
                          dataset,
                          chartDataManagerOptions,
                        );

                        if (queryMetadata && isComparingDateRange) {
                          queryMetadata.optionLabel =
                            formatTooltipDateForTimeComparison(dataset);
                        }

                        const compareNameAndIconHtml =
                          buildCompareNameAndIconHtml({
                            queryMetadata,
                            dataset,
                            hasComparisons: comparisons.length > 0,
                          });
                        return `
                          <th class="text-right text-xs font-semibold px-2 relative">
                            <span class="absolute left-0 h-3/4 border-l"></span>
                            <div class="font-normal mb-3">${compareNameAndIconHtml}</div>
                            <div>${selectedMetrics[0]?.name}</div>
                          </th>`;
                      })
                      .join("")
              }
            </tr>
          </thead>
          <tbody>
            ${dataHtml}
          </tbody>
        </table>`);
    }

    tooltipEl.innerHTML = buildTooltipContainer(html.join(""));
    if (isChartSplitView(chart) && tooltipEl.firstElementChild) {
      tooltipEl.firstElementChild.classList.remove("px-3");
    }

    const [x, y] = chartLabel.translation;
    const position = chart.canvas.getBoundingClientRect();
    const styles: CSSProperties = {
      opacity: "1",
      zIndex: "3000",
      position: "absolute",
      left: "",
      right: "",
      top: position.top + window.pageYOffset + y - 14 + "px",
      minWidth: "200px",
      pointerEvents: "none",
    };

    const tooltipCaretEl: any = tooltipEl.querySelector(".tooltip-caret");
    const tooltipLeftPosition = position.left + window.pageXOffset + x;

    const isOutOfBoundsRight =
      tooltipLeftPosition + tooltipEl.offsetWidth / 2 > window.innerWidth;
    const isOutOfBoundsLeft =
      tooltipLeftPosition - tooltipEl.offsetWidth / 2 < 0;
    if (isOutOfBoundsLeft && tooltipCaretEl) {
      tooltipCaretEl.classList.remove("left-1/2");
      tooltipCaretEl.style.left = tooltipLeftPosition + "px";
      styles.left = "0px";
      styles.transform = "translate(0%, -100%)";
    } else if (isOutOfBoundsRight && tooltipCaretEl) {
      tooltipCaretEl.classList.remove("left-1/2");
      const CARET_WIDTH = 12; // w-3 class on caret
      tooltipCaretEl.style.right = `${
        window.innerWidth - tooltipLeftPosition - CARET_WIDTH
      }px`;
      styles.right = "0px";
      styles.transform = "translate(0%, -100%)";
    } else {
      styles.left = tooltipLeftPosition + "px";
      styles.transform = "translate(-50%, -100%)";
    }

    Object.assign(tooltipEl.style, styles as CSSStyleDeclaration);

    // Hides the chart tooltip if it's visible
    const chartTooltip = document.getElementById("sales-v3-tooltip");
    if (chartTooltip && chartTooltip.style.opacity === "1") {
      chartTooltip.style.opacity = "0";
    }
  },
});
